// src/view/admin/PlantTimesheetReportView.tsx
import { TimesheetEmployeeRow } from "@interfaces/ITimesheetEmployeeRowDTO";
import { asDate } from "@utils/date";
import { useMemo } from "react";

export interface ViewProps {
  plants: Array<{ id: number; value: string }>;
  plantId?: number;
  plantName?: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  employees?: TimesheetEmployeeRow[];
  weekTotal?: number;
  loading?: boolean;
  error?: string | null;
  emailSendSuccess?: boolean;
  onPlantChange: (id: number) => void;
  onStartDateChange: (iso: string) => void;
  onEndDateChange: (iso: string) => void;
  onSubmit: () => void;
  onSendConfirm: () => void;
}

export function fmtMD(iso: string) {
  const d = asDate(iso);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}`;
}

export default function PlantTimesheetReportView({
  plants,
  plantId,
  plantName,
  startDate,
  endDate,
  employees = [],
  weekTotal,
  loading,
  error,
  emailSendSuccess = false,
  onPlantChange,
  onStartDateChange,
  onEndDateChange,
  onSubmit,
  onSendConfirm,
}: ViewProps) {
  const dayCols = useMemo(() => {
    const start = asDate(startDate);
    const keys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];
    return keys.map((k, i) => {
      if (!start) return { key: k, label: labels[i], date: "" };
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      return { key: k, label: labels[i], date: fmtMD(iso) };
    });
  }, [startDate]);

  const hasResults = employees.length > 0;

  const CELL_PAD = "tw-px-0.25 tw-py-0.5 sm:tw-px-2 sm:tw-py-2"; // reduced padding on mobile
  const NUM_CELL = `${CELL_PAD} tw-text-center tw-border-r tw-whitespace-nowrap tw-tabular-nums tw-text-[0.65rem] sm:tw-text-[0.85rem]`;
  const HEAD_TXT =
    "tw-text-[10px] sm:tw-text-xs tw-font-semibold tw-text-black";

  // widths tuned for mobile, scale up on sm+
  const W_CID = "tw-w-[40px] sm:tw-w-[80px] tw-shrink-0";
  const W_NAME =
    "tw-w-auto tw-min-w-[80px] sm:tw-min-w-[120px] tw-max-w-[150px] sm:tw-max-w-[180px] tw-flex-1"; // adaptive width based on content
  const W_DAY = "tw-w-[34px] sm:tw-w-[56px] tw-shrink-0";
  const W_TOTAL = "tw-w-[35px] sm:tw-w-[72px] tw-shrink-0 tw-grow-0";

  return (
    <div className="tw-w-full tw-max-w-6xl tw-mt-5 tw-mx-auto tw-px-3 tw-py-4">
      {error && <p className="tw-text-red-600 tw-mb-3">{error}</p>}

      {/* Period form (simple) */}
      <div className="tw-bg-white tw-rounded-2xl tw-shadow tw-p-3 tw-space-y-3">
        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-sm tw-font-medium" htmlFor="plant">
            Plant:
          </label>
          <select
            id="plant"
            className="tw-border tw-rounded-lg tw-px-3 tw-py-2 tw-bg-white"
            value={plantId ?? ""}
            onChange={(e) => onPlantChange(Number(e.target.value))}
          >
            <option value="" disabled>
              Select a plant
            </option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.value}
              </option>
            ))}
          </select>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-sm tw-font-medium" htmlFor="start-date">
            Start Date (Sunday only):
          </label>
          <input
            id="start-date"
            name="start-date"
            type="date"
            className="tw-border tw-rounded-lg tw-px-3 tw-py-2"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
          />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-sm tw-font-medium" htmlFor="end-date">
            End Date:
          </label>
          <input
            id="end-date"
            name="end-date"
            type="date"
            className="tw-border tw-rounded-lg tw-px-3 tw-py-2"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
          />
        </div>

        <div className="tw-flex tw-gap-2">
          <button
            className="tw-w-full tw-rounded-xl tw-shadow tw-border tw-py-2 tw-px-6 tw-font-medium
                       tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700
                       focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-2
                       tw-disabled:opacity-50 tw-transition-fast"
            onClick={onSubmit}
            disabled={loading || !plantId || !startDate || !endDate}
          >
            {loading ? "Loading..." : "Submit"}
          </button>
        </div>
      </div>

      {/* SEMANTIC TABLE WITH FLEX STYLING */}
      {hasResults && (
        <div className="tw-mt-3 tw-bg-white tw-shadow tw-overflow-x-auto tw-w-full">
          <div className="tw-flex tw-justify-center">
            <table className="tw-border-collapse tw-table-fixed tw-mx-auto">
              <thead>
                <tr
                  className={`${HEAD_TXT} tw-bg-gray-50 tw-border-b tw-flex tw-items-stretch`}
                >
                  <th
                    className={`${W_CID} ${CELL_PAD} tw-border-r tw-leading-tight tw-font-semibold tw-text-center`}
                  >
                    <span className="sm:tw-hidden">CID</span>
                    <span className="tw-hidden sm:tw-inline tw-whitespace-normal tw-hyphens-auto">
                      Contractor ID
                    </span>
                  </th>

                  <th
                    className={`${W_NAME} ${CELL_PAD} tw-border-r tw-leading-tight tw-font-semibold tw-text-center`}
                  >
                    Name
                  </th>

                  {dayCols.map((d) => (
                    <th
                      key={d.key}
                      className={`${W_DAY} ${CELL_PAD} tw-text-center tw-border-r tw-leading-tight tw-whitespace-nowrap tw-font-semibold`}
                      scope="col"
                    >
                      {/* xs: single letter; sm+: full label + date */}
                      <span className="sm:tw-hidden">{d.label[0]}</span>
                      <div className="tw-hidden sm:tw-block">
                        <div>{d.label}</div>
                        <div className="tw-text-[10px] tw-font-normal tw-text-gray-700">
                          {d.date}
                        </div>
                      </div>
                    </th>
                  ))}

                  <th
                    className={`${W_TOTAL} ${CELL_PAD} tw-text-center tw-leading-tight tw-font-semibold`}
                    scope="col"
                  >
                    <span className="sm:tw-hidden">Tot</span>
                    <span className="tw-hidden sm:tw-inline">Total</span>
                  </th>
                </tr>
              </thead>

              <tbody className="tw-divide-y">
                {employees.map((emp) => (
                  <tr
                    key={emp.id ?? `${emp.contractor_id}-${emp.name}`}
                    className="tw-flex tw-items-center tw-text-[0.7rem] sm:tw-text-sm"
                  >
                    <td
                      className={`${W_CID} ${CELL_PAD} tw-border-r tw-text-center tw-leading-tight tw-whitespace-nowrap`}
                    >
                      {emp.contractor_id}
                    </td>

                    <td
                      className={`${W_NAME} ${CELL_PAD} tw-border-r tw-whitespace-normal tw-break-words tw-leading-tight`}
                      title={emp.name}
                    >
                      {emp.name}
                    </td>

                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.sun_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.mon_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.tue_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.wed_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.thu_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.fri_hour}</td>
                    <td className={`${W_DAY} ${NUM_CELL}`}>{emp.sat_hour}</td>

                    <td
                      className={`${W_TOTAL} ${CELL_PAD} tw-text-right tw-font-semibold tw-whitespace-nowrap`}
                    >
                      {emp.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Week total + Send confirm (only when results) */}
      {hasResults && (
        <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-2">
          <div className="tw-text-sm tw-font-medium">
            Week Total: {weekTotal ?? 0}
          </div>

          <button
            className={`tw-w-full tw-rounded-xl tw-shadow tw-border tw-py-2 tw-px-6 tw-font-medium
                       ${
                         emailSendSuccess
                           ? "tw-bg-emerald-600 tw-text-white"
                           : loading
                           ? "tw-bg-blue-500 tw-text-white"
                           : "tw-bg-green-600 tw-text-white hover:tw-bg-green-700"
                       }
                       focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-2
                       tw-disabled:opacity-50 tw-transition-colors tw-duration-200`}
            onClick={onSendConfirm}
            disabled={
              loading || !plantId || !startDate || !endDate || !employees.length
            }
          >
            {loading
              ? "Sending..."
              : emailSendSuccess
              ? "✓ Emails Sent!"
              : "Send Conf Emails"}
          </button>
        </div>
      )}
    </div>
  );
}
